import { type JSX, type ReactNode, useEffect, useRef } from 'react';
import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { useSession } from './useSession.js';
import { useSignOut } from './useSignOut.js';

const messageClasses =
  'rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function SessionStatusBoundary({ children }: { readonly children: ReactNode }): JSX.Element {
  const session = useSession();
  const t = useTranslate();
  const signOut = useSignOut();
  const messageRef = useRef<HTMLParagraphElement>(null);
  const kind = session.state.kind;
  useEffect(() => {
    if (kind === 'signingOut' || kind === 'signOutFailed') messageRef.current?.focus();
  }, [kind]);
  // Distinct keys mount a new message node, so focus really moves to the failure.
  if (session.state.kind === 'signingOut') {
    return (
      <main key="signingOut" className="p-6">
        <p ref={messageRef} tabIndex={-1} role="status" className={messageClasses}>
          {t('shell.session.signingOut')}
        </p>
      </main>
    );
  }
  if (session.state.kind === 'signOutFailed') {
    return (
      <main key="signOutFailed" className="mx-auto flex max-w-lg flex-col gap-4 p-6">
        <p ref={messageRef} tabIndex={-1} role="alert" className={messageClasses}>
          {t('shell.session.signOutFailed')}
        </p>
        <Button type="button" onClick={() => void signOut()}>
          {t('shell.session.retrySignOut')}
        </Button>
      </main>
    );
  }
  return <>{children}</>;
}

/** Shown instead of member content while the page reloads for a replacing account. */
export function SessionReplacedNotice({ reload }: { readonly reload: () => void }): JSX.Element {
  const t = useTranslate();
  const messageRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    messageRef.current?.focus();
  }, []);
  return (
    <main className="mx-auto flex max-w-lg flex-col gap-4 p-6">
      <p ref={messageRef} tabIndex={-1} role="status" className={messageClasses}>
        {t('shell.session.accountReplaced')}
      </p>
      <Button type="button" onClick={reload}>
        {t('shell.session.reload')}
      </Button>
    </main>
  );
}
